<template>
  <div
    id="app"
    data-mri="영업 링크 귀속 연동 — 레거시 고객 웹"
  >
    <!-- 스플래시 광고 -->
    <SplashAd v-if="showSplashAd && !isSherlockRoute" />

    <!-- 기존 내집스캔 -->
    <template v-if="!isSherlockRoute && !isInit">
      <SideArticleReview />
      <router-view
        class=""
        :key="$route.fullPath"
      />
      <FooterNavigation
        ref="footerNavigation"
        v-show="_state.footerBool && !hideFooterForDabang"
      />
      <LoadingOverlay v-if="_state.loadingBool" />
      <transition name="slide-fade">
        <ToasMessage v-if="_state.toastBool" />
      </transition>
      <Alert v-if="_modal.modalState" />
      <AppUpdateModal v-if="_app_update_modal" />
      <NotificationPopup
        v-if="showNotificationPopup"
        @close="
          () => {
            isNotificationPopupClosed = true;
          }
        "
      />
      <!-- 홈 팝업 광고 -->
      <PopupAd
        v-if="showPopupAd"
        @close="isPopupAdClosed = true"
      />
    </template>

    <!-- Sherlock 페이지일 경우 -->
    <template v-else-if="isSherlockRoute && !isInit">
      <router-view
        class=""
        :key="$route.fullPath"
      />
    </template>
  </div>
</template>

<script src="https://cpay.payple.kr/js/cpay.payple.1.0.1.js"></script>
<script>
import { mapActions, mapState, mapMutations } from "vuex";
import ChannelService from "@/mixins/ChannelService";
import LoadingOverlay from "@/components/common/Loading.vue";
import ToasMessage from "@/components/common/ToastMessage.vue";
import Alert from "@/components/common/Alert.vue";
import FooterNavigation from "@/components/footer/navigation.vue";
import SideArticleReview from "@/components/side-article-landing.vue";
import NotificationPopup from "@/components/settings/notification-popup.vue";
import AppUpdateModal from "@/components/settings/app-update-modal.vue";
import { PUSH_NOTI_ACTION } from "@/enum/push-noti";
import { createNativeBottomInsetReceiver } from "@/utils/nativeBottomInset";
import { ViewRouter } from "@/utils/viewRoutes";
import { DOBY_STATUS } from "./enum/doby";
import SplashAd from "@/components/advertisement/SplashAd.vue";
import PopupAd from "@/components/advertisement/PopupAd.vue";
import { supportsSplashAd } from "@/components/advertisement/adTypes";
import { isMobile } from "@/utils/statusCheck";
import { bindCurrentSalesAttribution } from "@/utils/salesAttribution";
import {
  alfiePushRequest,
  bootAlfie,
  isAlfieEnabled,
  isDabangSession as isAlfieDabangSession,
  openAlfie,
  refreshAlfieIdentity,
  setAlfieLauncherOffset,
} from "@/utils/alfieWidget";
import { detectDevice } from "@/utils/payAttemptLog";
import { safeReturnPath } from "@/utils/authReturn";

const AD_SPLASH_SHOWN_KEY = "ad_splash_shown";

export default {
  name: "App",
  watch: {
    $route(to) {
      bindCurrentSalesAttribution();
      void this.acSyncContractType();
      // 광고 API가 늦게 도착해도 다음 화면(로딩·결제 등)에 팝업이 뒤늦게 올라오지 않게 한다.
      if (to.fullPath !== this.popupAdInitialRoute) {
        this.isPopupAdSuppressedAfterNavigation = true;
      }
      this.fnSyncAlfie();
    },
    "_state.footerBool"() {
      this.fnSyncAlfie();
    },
  },
  components: {
    LoadingOverlay,
    ToasMessage,
    Alert,
    FooterNavigation,
    SideArticleReview,
    NotificationPopup,
    AppUpdateModal,
    SplashAd,
    PopupAd,
  },
  created() {
    this.popupAdInitialRoute = this.$route.fullPath;
    // [NZSC-3087 진단용 trace — 가스앱 webview brook 누락 원인 파악 후 제거]
    this.fnGasappTrace("created");
    this.fnInit();
  },
  data() {
    return {
      isInit: true,
      isThrottle: false,
      isNewDevice: false,
      isNotificationPopupClosed: false,
      featureFlag_notificationPopup: null,
      showSplashAd: false,
      isPopupAdClosed: false,
      adLoaded: false,
      splashAdTimer: null,
      popupAdInitialRoute: "",
      isPopupAdSuppressedAfterNavigation: false,
      // 알피 위젯에 마지막으로 알린 로그인 토큰(바뀌면 신원 다시 확인)
      alfieSessionToken: null,
    };
  },
  mounted() {},
  beforeDestroy() {
    if (this.splashAdTimer) {
      clearTimeout(this.splashAdTimer);
    }
    this.fnCleanupWebViewMessageHandler();
  },
  computed: {
    ...mapState(["_state", "_modal"]),
    ...mapState("userStore", ["_channelTalk", "_deviceInfo", "_email"]),
    ...mapState("settingsStore", ["_permissionsInfo"]),
    ...mapState("campaignStore", ["_nz_source"]),
    ...mapState("settingsStore", ["_app_update_modal", "_is_new_device"]),
    ...mapState("advertisementStore", {
      adStoreLoaded: "_loaded",
      adResolvedType: "_resolvedAdType",
    }),
    showPopupAd() {
      if (this.isDabangSession) return false; // 다방 인앱 진입 — 자체 광고 미노출 (NZSC-3205)
      // 권원보험 퍼널은 청약·결제·완료까지 하나의 전환 흐름이므로, 같은 상품 유도 팝업을 노출하지 않는다.
      if (this.isFirstamericanRoute) return false;
      if (this.isPopupAdSuppressedAfterNavigation) return false;
      const suspendUntil = localStorage.getItem("ad_popup_suspend_until");
      if (suspendUntil && new Date(suspendUntil) > new Date()) return false;
      if (!this.adLoaded || !this.adResolvedType) return false;
      if (this.showSplashAd) return false;
      if (this.isPopupAdClosed) return false;
      return true;
    },
    isFirstamericanRoute() {
      return this.$route.path.startsWith("/insurance/firstamerican/");
    },
    supportsSplashAd() {
      return supportsSplashAd(this.adResolvedType);
    },
    // 다방 인앱 세션 여부 — 진입 처리(DabangEnter) 완료 플래그 또는 진입 라우트 자체.
    // sessionStorage 라 웹뷰 생명주기 동안 유지, 일반 브라우저 세션엔 영향 없음.
    isDabangSession() {
      return (
        this.$route.path.startsWith("/dabang") ||
        window.sessionStorage.getItem("dabang_entered") === "1"
      );
    },
    // 다방 인입 샌드박스 정책(NZSC-3205, 2026-07-22 확정): 다방 세션은 주소퍼널~결제완료~리포트 화면만
    // 볼 수 있고 그 밖(마이페이지·홈 등)으로 이동 불가. 푸터 네비(홈·마이페이지 탭)는 이탈 수단이므로
    // 다방 세션 전 구간에서 미노출한다(종전엔 결제화면에서만 숨겨 리포트·완료 등에서 이탈 가능했음).
    hideFooterForDabang() {
      return this.isDabangSession;
    },
    isSherlockRoute() {
      // jasanoneclick.com 도메인에서만 sherlock 라우트로 인식
      if (typeof window === "undefined") return false;
      const isSherlockState =
        this.$route.query.state === "sherlock" || this.$route.query.state === "sherlock_freetrial";
      if (process.env.NODE_ENV === "development") {
        return this.$route.path.startsWith("/sherlock") || isSherlockState;
      }
      const hostname = window.location.hostname;
      const isJasanoneclickDomain =
        hostname === "jasanoneclick.com" || hostname.endsWith(".jasanoneclick.com");
      return isJasanoneclickDomain && (this.$route.path.startsWith("/sherlock") || isSherlockState);
    },
    isNotificationPopupEnabled() {
      const enabled = this.featureFlag_notificationPopup?.data?.enabled === true;
      const platformEnabled =
        (this._deviceInfo?.platform === "android" &&
          this.featureFlag_notificationPopup?.data?.android === true) ||
        (this._deviceInfo?.platform === "ios" &&
          this.featureFlag_notificationPopup?.data?.ios === true);
      return enabled && platformEnabled;
    },
    isNotificationPermissionGranted() {
      return this._permissionsInfo?.notification === "PermissionStatus.granted";
    },
    isCameraPermissionGranted() {
      return this._permissionsInfo?.camera === "PermissionStatus.granted";
    },
    hasDevicePushToken() {
      return this._deviceInfo?.push_token?.length > 0;
    },
    // [NZSC-3087 진단용 — utm_source=gasapp 트래픽만 trace 발사. 다른 트래픽 노이즈 0]
    isGasappTraffic() {
      try {
        return new URLSearchParams(window.location.search).get("utm_source") === "gasapp";
      } catch {
        return false;
      }
    },
  },
  methods: {
    ...mapActions(["acCheckToastState", "acRequestRefreshToken"]),
    ...mapActions("contractTypeStore", ["acSyncContractType"]),
    ...mapActions("userStore", [
      "acChannelTalk",
      "acSetDeviceInfo",
      "acRegisterPushDevice",
      "acAlfieIdentityToken",
    ]),
    ...mapActions("settingsStore", ["acSetPermissionsInfo"]),
    ...mapActions("campaignStore", ["acInitializeNzSource"]),
    ...mapActions("brookStore", ["acBrookCampaign", "acBrookTrackingEvent"]),
    ...mapActions("settingsStore", ["acGetAppUpdate", "acGetIsNewDevice"]),
    ...mapActions("featureFlagStore", ["acEvalFeatureFlag"]),
    ...mapActions("reportStore", ["acReportPre"]),
    ...mapActions("advertisementStore", ["acFetchAdvertisement"]),
    ...mapMutations(["setFooterBoolState", "setFlutterWebViewReady"]),
    fnInit() {
      bindCurrentSalesAttribution();
      void this.acSyncContractType();
      // [NZSC-3087 진단용 trace — 가스앱 webview brook 누락 원인 파악 후 제거]
      this.fnGasappTrace("fnInit_enter", { isSherlockRoute: this.isSherlockRoute });
      // NZSC-3252 iOS 자동로그인 복구: iOS ITP가 localStorage(naezip_to_ac_se)를 7일 캡으로
      // 소거해도 HttpOnly refresh 쿠키는 살아있을 수 있다. 토큰이 "없을 때만" 1회 복구 시도.
      // (과거 bd381678의 무조건 실행과 달리 가드형 — 로그인 상태·로그인 콜백 흐름에 영향 없음.
      //  쿠키가 없으면 acRequestRefreshToken이 조용히 false를 반환해 기존 비로그인 동작과 동일.)
      if (!window.localStorage.getItem("naezip_to_ac_se")) {
        this.acRequestRefreshToken();
      }
      if (!this.isSherlockRoute) {
        this._state.footerBool = true;
        this.acCheckToastState({ type: false, msg: "" });
        this.acInitializeNzSource();
        // [NZSC-3087 진단용 trace]
        this.fnGasappTrace("after_initNzSource");
        try {
          // 채널톡 플로팅: 리포트 미리보기 샘플(/report/sample/*)에서만 미노출(다방 요청 2026-07-23).
          // 그 외 전부 노출 — 다방 인앱 세션 포함(다방이 채널톡 재노출 요청, 07-22 미노출에서 번복).
          // 샘플은 진입토큰·세션 없이 URL 직접 공유되는 페이지라 계속 숨김.
          // created 시점이라 $route 미초기화 가능 → window.location 으로 판별.
          // ChannelIO 스텁은 import 시 loadScript 로 항상 생성되므로 미부팅이어도 타 호출은 no-op(에러 없음).
          const isChannelHidden = window.location.pathname.startsWith("/report/sample");
          // 알피 고객센터가 켜진 환경(VUE_APP_ALFIE_CHAT_ORIGIN)이면 채널톡 대신 알피 위젯(KRE-38)
          if (!isChannelHidden && isAlfieEnabled()) {
            this.fnBootAlfie();
          } else if (!isChannelHidden) {
            ChannelService.boot();
            ChannelService.onShowMessenger(this.fnChannelTalk);
          }
          // [NZSC-3087 진단용 trace]
          this.fnGasappTrace("after_channelService");
        } catch (e) {
          // [NZSC-3087 진단용 trace — channel service throw 잡기]
          this.fnGasappTrace("channelService_throw", { err: e && e.message });
        }
        this.fnSetupWebViewMessageHandler();
        // [NZSC-3087 진단용 trace]
        this.fnGasappTrace("after_setupWebViewHandler");
        this.fnBrookCampaign();
        // [NZSC-3087 진단용 trace — 동기적으로 fnBrookCampaign 호출만 보내고 return된 시점]
        this.fnGasappTrace("after_brookCampaignCall");
        this.fnGetAppUpdate();
        this.fnGetAppSystemInfo();
        this.fnCheckIsNewDevice();
        this.fnFeatureFlag();
        this.fnInitAdvertisement();
      }
      setTimeout(() => {
        this.isInit = false;
        this.fnSyncAlfie();
      }, 500);
    },
    // [NZSC-3087 진단용 — 가스앱 webview에서 brook 어디서 끊기는지 추적. 진단 후 제거]
    fnGasappTrace(stage, extra = {}) {
      if (!this.isGasappTraffic) return;
      try {
        this.$sentryMessage("gasapp_trace", `[gasapp_trace] ${stage}`, {
          stage,
          url: window.location.href,
          referrer: document.referrer,
          ua: (navigator.userAgent || "").slice(0, 200),
          time: Date.now(),
          ...extra,
        });
        // eslint-disable-next-line no-console
        console.log(`[gasapp_trace] ${stage}`, extra);
      } catch (e) {
        // silent — 진단 코드라 어떤 일이 있어도 본 흐름 방해 X
      }
    },
    async fnInitAdvertisement() {
      const SPLASH_DURATION_MS = 1000;
      try {
        await this.acFetchAdvertisement();
        this.adLoaded = true;

        // 스플래시: 모바일 + 콜드 스타트(sessionStorage 없을 때)만 표시. 다방 인앱 진입은 제외(NZSC-3205).
        const isColdStart = !sessionStorage.getItem(AD_SPLASH_SHOWN_KEY);
        if (isMobile() && isColdStart && this.supportsSplashAd && !this.isDabangSession) {
          this.showSplashAd = true;
          sessionStorage.setItem(AD_SPLASH_SHOWN_KEY, "true");
          this.splashAdTimer = setTimeout(() => {
            this.showSplashAd = false;
          }, SPLASH_DURATION_MS);
        }
      } catch (error) {
        console.log("fnInitAdvertisement error:", error);
        this.adLoaded = false;
      }
    },
    async fnBrookCampaign() {
      // [NZSC-3087 진단용 trace]
      this.fnGasappTrace("brook_start");
      try {
        // [NZSC-3087 진단용 trace — POST 직전, 어떤 referrer를 보내는지 추적]
        this.fnGasappTrace("brook_campaign_call", { referrer: window.location.href });
        const brookCampaignRes = await this.acBrookCampaign({
          referrer: window.location.href,
          items: null,
        });
        // [NZSC-3087 진단용 trace — API 응답 후. resLength=0이면 매칭 실패, null이면 fetch 실패]
        this.fnGasappTrace("brook_campaign_response", {
          resLength:
            brookCampaignRes && brookCampaignRes.length != null ? brookCampaignRes.length : null,
          resIsArray: Array.isArray(brookCampaignRes),
        });
        // window.console.log("brookCampaignRes", brookCampaignRes);
        if (brookCampaignRes?.length) {
          for (const campaign of brookCampaignRes) {
            // [NZSC-3087 진단용 trace — 매칭된 캠페인 정보]
            this.fnGasappTrace("brook_campaign_match", {
              provider: campaign.provider,
              channel: campaign.channel,
              tracking_id: campaign.tracking_id,
            });
            const trackingId = localStorage.getItem(campaign.tracking_id);
            // [NZSC-3087 진단용 trace — localStorage dedup 체크]
            this.fnGasappTrace("brook_localStorage_check", { alreadyTracked: !!trackingId });
            if (!trackingId) {
              // [NZSC-3087 진단용 trace]
              this.fnGasappTrace("brook_tracking_call");
              const trackingRes = await this.acBrookTrackingEvent({
                provider: campaign.provider,
                channel: campaign.channel,
                device_id: await this.$deviceId(),
                referrer: window.location.href,
              });
              // [NZSC-3087 진단용 trace]
              this.fnGasappTrace("brook_tracking_response", {
                exists: !!(trackingRes && trackingRes.tracking_id),
              });
              if (trackingRes?.tracking_id?.length && !localStorage.getItem("naezip_to_ac_se")) {
                localStorage.setItem(campaign.tracking_id, JSON.stringify(trackingRes));
                // [NZSC-3087 진단용 trace]
                this.fnGasappTrace("brook_local_set");
              }
            }
          }
        } else {
          // [NZSC-3087 진단용 trace — 매칭 0건 (utm_medium 누락 등으로 서버에서 빈 array 반환)]
          this.fnGasappTrace("brook_campaign_empty");
        }

        // 인블로그에서 유입 트래킹
        try {
          const ref = document.referrer;
          if (ref) {
            const refUrl = new URL(ref);
            if (
              refUrl.origin === window.location.origin &&
              refUrl.pathname.startsWith("/blog") &&
              !localStorage.getItem("inblog_nzsc_id")
            ) {
              const blogReferrer =
                window.location.origin + "/blog?utm_source=inblog&utm_medium=inblog";
              const blogRes = await this.acBrookCampaign({ referrer: blogReferrer, items: null });
              if (blogRes?.length) {
                for (const campaign of blogRes) {
                  if (!localStorage.getItem(campaign.tracking_id)) {
                    const trackingRes = await this.acBrookTrackingEvent({
                      provider: campaign.provider,
                      channel: campaign.channel,
                      device_id: await this.$deviceId(),
                      referrer: blogReferrer,
                    });
                    if (
                      trackingRes?.tracking_id?.length &&
                      !localStorage.getItem("naezip_to_ac_se")
                    ) {
                      localStorage.setItem(campaign.tracking_id, JSON.stringify(trackingRes));
                    }
                  }
                }
              }
            }
          }
        } catch {}
        // [NZSC-3087 진단용 trace — 정상 완료]
        this.fnGasappTrace("brook_complete");
      } catch (e) {
        // [NZSC-3087 진단용 trace — fetch 또는 다른 단계에서 throw. 에러 메시지가 진짜 원인]
        this.fnGasappTrace("brook_catch", {
          err: e && e.message,
          name: e && e.name,
          stack: (e && e.stack ? e.stack : "").slice(0, 500),
        });
      }
    },
    async fnGetAppUpdate() {
      if (this.hasDevicePushToken) {
        await this.acGetAppUpdate({
          device_push_token: this._deviceInfo.push_token,
        });
      }
    },
    fnBootAlfie() {
      const app = detectDevice() === "app";
      // 다방 인앱 세션: 진입 컨텍스트 'dabang' + 문의 리포트 코드(노션: 다방 유입은 리포트로 고객 매칭)
      const dabang = isAlfieDabangSession();
      const reportCode = dabang
        ? new URLSearchParams(window.location.search).get("report_code")
        : null;
      bootAlfie({
        app,
        entryKind: dabang ? "dabang" : undefined,
        // 다방 문의 페이지는 토큰 교환 뒤 DabangSupport 가 releaseAlfieBoot() 로 띄운다(첫 판정이 그 신원으로)
        defer: window.location.pathname.startsWith("/dabang/support"),
        reportSeq: reportCode || undefined,
        getIdentityToken: () => this.acAlfieIdentityToken(),
        onLoginRequest: this.fnAlfieLogin,
        // 오픈 전 내부 테스트(알피 인입 internal) 명단 밖 고객에게는 지금처럼 채널톡(콜백은 지우고 한 번만 건다)
        channelTalk: {
          start: () => {
            ChannelService.startChannelTalk();
            ChannelService.clearCallbacks();
            ChannelService.onShowMessenger(this.fnChannelTalk);
          },
          stop: () => ChannelService.shutdown(),
        },
        notifications: app
          ? {
              isOff: this.fnAlfieNotificationsOff,
              openSettings: this.fnOpenAppNotificationSettings,
            }
          : undefined,
      });
      this.alfieSessionToken = localStorage.getItem("naezip_to_ac_se");
      this.fnSyncAlfie();
    },
    // 화면 이동·하단 메뉴 표시가 바뀔 때: 로그인 상태가 바뀌었으면 신원 다시 확인, 플로팅 버튼을 하단 메뉴 위로
    fnSyncAlfie() {
      if (!isAlfieEnabled()) return;
      const token = localStorage.getItem("naezip_to_ac_se");
      if (token !== this.alfieSessionToken) {
        this.alfieSessionToken = token;
        refreshAlfieIdentity();
      }
      this.$nextTick(() => {
        const footer = this.$refs.footerNavigation?.$el?.querySelector?.(".nav_fixed");
        setAlfieLauncherOffset(footer ? footer.offsetHeight : 0);
      });
    },
    // 로그인 연결 카드 '로그인하고 이어보기' — 로그인 뒤 지금 화면으로 돌아온다
    fnAlfieLogin() {
      // 다방 요구(2026-07-28): 상담창 밖 내집스캔 화면으로 보내지 않는다
      if (isAlfieDabangSession()) return;
      const ret = safeReturnPath(this.$route.fullPath);
      this.$router.push({ path: "/login", query: ret ? { return: ret } : {} });
    },
    // 앱 기기 알림이 꺼져 있는가 — 앱이 아직 권한을 안 알려 줬으면 꺼졌다고 하지 않는다
    async fnAlfieNotificationsOff() {
      const status = this._permissionsInfo?.notification;
      if (!status) return false;
      return status !== "PermissionStatus.granted";
    },
    fnOpenAppNotificationSettings() {
      if (this._state.flutterWebViewReady) {
        window.flutter_inappwebview.callHandler("openAppNotificationSettings").then(function () {});
      }
    },
    async fnChannelTalk() {
      const user = await this.acChannelTalk();
      if (user) {
        const { email, mobile, order_count, report_status } = this._channelTalk;
        ChannelService.updateUserObject(email, mobile, order_count, report_status);
      }
    },
    async fnGetAppSystemInfo() {
      if (this._state.flutterWebViewReady) {
        window.flutter_inappwebview.callHandler("getAppSystemInfo").then(function (result) {});
      }
    },
    async fnCheckIsNewDevice() {
      // FCM Token이 등록된 적 없는 신규 기기인지 여부 확인.
      // NZSC-3269: 웹 브라우저는 push_token이 없어 항상 400 응답 — fnGetAppUpdate와 동일하게
      // 앱 웹뷰(토큰 보유)에서만 호출. 웹 동작은 스킵 전과 동일(isNewDevice=false 유지).
      if (!this.hasDevicePushToken) {
        return;
      }
      const isNewDevice = await this.acGetIsNewDevice({
        device_push_token: this._deviceInfo.push_token,
      });
      // window.console.log("isNewDevice: ", isNewDevice);
      this.isNewDevice = isNewDevice;
    },
    async fnSetupPushNotification() {
      // 푸쉬 알림 등록 세팅.
      this.acRegisterPushDevice();
    },
    /** 모바일 앱에서 푸쉬알림용 토큰 전달 이벤트 핸들러. */
    fnWebViewMessageHandler(event) {
      const { data } = event;
      // window.console.log("WEBVIEW EVENT: ", JSON.stringify(event));
      // window.console.log("WEBVIEW DATA: ", JSON.stringify(data));
      if (data === "capturePort") {
        // capture port2 coming from the Dart side
        if (event.ports[0] != null) {
          // the port is ready for communication,
          // so you can use port.postMessage(message); wherever you want
          const port = event.ports[0];
          const receiveInsetOwnership = createNativeBottomInsetReceiver();
          // To listen to messages coming from the Dart side, set the onmessage event listener
          port.onmessage = async event => {
            // event.data contains the message data coming from the Dart side
            // Native payload contains push identifiers; do not log it.
            const parsedData = event.data ? JSON.parse(event.data) : null;
            window.console.log("WEBVIEW SENT DATA MSGTYPE: ", parsedData?.msgtype);
            if (parsedData?.msgtype === "app_device_info") {
              receiveInsetOwnership(parsedData);
              try {
                const deviceInfo = {
                  push_token: parsedData.push?.fcmToken,
                  platform: parsedData.device?.platform,
                  os_version: parsedData.device?.osVersion,
                  device_info: parsedData.device?.info,
                  app_name: parsedData.app?.appName,
                  app_version: parsedData.app?.version,
                  app_build_number: parsedData.app?.buildNumber,
                  app_install_time: parsedData.app?.installTime,
                  app_update_time: parsedData.app?.updateTime,
                };
                const token = localStorage.getItem("naezip_to_ac_se");
                // window.console.log(
                //   "PUSH TOKEN DEVICE: ",
                //   token,
                //   JSON.stringify(deviceInfo)
                // );
                this.acSetDeviceInfo(deviceInfo);
                this.fnSetupPushNotification();
              } catch (error) {
                window.console.log("app_device_info error: ", error);
              }
            } else if (parsedData?.msgtype === "app_system_info") {
              // 앱 시스템 정보 수신.
              const permissionsInfo = {
                notification: parsedData.permissionsInfo?.notification,
                camera: parsedData.permissionsInfo?.camera,
              };
              this.acSetPermissionsInfo(permissionsInfo);
            } else if (parsedData?.msgtype === "push_notification") {
              // TODO: REMOVE: 260211: 푸쉬 알림 데이터 확인용 테스트 코드.
              if (this._email === "etture@gmail.com") {
                alert("PUSH NOTI WEBVIEW SENT DATA: " + JSON.stringify(event.data));
              }

              // 푸쉬 알림 처리 로직.
              const payload = parsedData?.payload;
              // 알피 고객센터 답변 → 화면 이동 없이 위젯을 그 대화방으로 연다(KRE-38)
              const alfieRequest = alfiePushRequest(payload);
              if (alfieRequest && isAlfieEnabled()) {
                openAlfie(alfieRequest);
                return;
              }
              let route = null;
              switch (payload?.push_action) {
                case PUSH_NOTI_ACTION.REPORT.REPORT_ISSUED:
                  const contractType = payload?.contract_type;
                  if (contractType === "매매") {
                    route = ViewRouter.getBuyReportRoute({
                      encrypted_order_seq: payload?.encrypted_order_seq,
                      encrypted_cart_seq: payload?.encrypted_cart_seq,
                      is_report_owner: true,
                      is_pass: false,
                    });
                    this.$mixpanel_track("View Sale Report-Click Alert");
                  } else {
                    route = ViewRouter.getJeonseReportRoute({
                      encrypted_order_seq: payload?.encrypted_order_seq,
                      encrypted_cart_seq: payload?.encrypted_cart_seq,
                      is_report_owner: true,
                      is_pass: false,
                    });
                    this.$mixpanel_track("View Report-analyzed-Click Alert");
                  }
                  break;
                case PUSH_NOTI_ACTION.REPORT.OWNER_HOUSE_ISSUED:
                  route = ViewRouter.getOwnerOtherHouseViewRoute({
                    encrypted_report_seq: payload?.encrypted_report_seq,
                  });
                  this.$mixpanel_track("View Report-Owner Other house-Click Alert");
                  break;
                case PUSH_NOTI_ACTION.REPORT.BUY_REPORT_MORTGAGE_CONSULT_NUDGE: {
                  const nudgeReport = await this.acReportPre(
                    encodeURIComponent(payload?.encrypted_cart_seq),
                  );
                  route = ViewRouter.getHouseLoanConsultationRoute(nudgeReport?.reportSeq);
                  this.$mixpanel_track("View Sale Chat-Click Alert");
                  break;
                }
                case PUSH_NOTI_ACTION.REPORT.LOAN_CONSULT_AGENT_REPLY:
                  // 대출상담 상담사 답변 → 해당 리포트의 실시간 상담 채팅으로 이동
                  route = payload?.report_seq
                    ? { path: "/loan/relay-chat", query: { reportSeq: String(payload.report_seq) } }
                    : "/loan/relay-chat";
                  this.$mixpanel_track("View Loan Consult Chat-Click Alert");
                  break;
                case PUSH_NOTI_ACTION.REPORT.FIRSTAM_CS_AGENT_REPLY:
                  // FA 고객센터 작업자 답변 → 해당 청약(리포트)의 고객센터 채팅으로 이동
                  route = payload?.report_seq
                    ? {
                        path: "/insurance/firstamerican/title-001/customer-center",
                        query: { reportSeq: String(payload.report_seq) },
                      }
                    : "/insurance/firstamerican/title-001/customer-center";
                  this.$mixpanel_track("View Firstam CS Chat-Click Alert");
                  break;
                case PUSH_NOTI_ACTION.REPORT.FIRSTAM_SALES_AGENT_REPLY:
                  // 안심 매매 패키지 채팅 담당자 답변 → 해당 상담 채팅으로 이동.
                  // 이 채널은 리포트에 매달리지 않는 랜딩 상담이라 report_seq 가 없다. 상담 식별자로 연다.
                  route = payload?.consultation_id
                    ? {
                        path: "/insurance/firstamerican/title-001/chat",
                        query: { c: String(payload.consultation_id), from: "push" },
                      }
                    : "/insurance/firstamerican/title-001/chat/list";
                  this.$mixpanel_track("View Firstam Sales Chat-Click Alert");
                  break;
                case PUSH_NOTI_ACTION.SUBSCRIBE.CHANGE_DETECTED:
                  if ([true, "true"].includes(payload?.is_premium)) {
                    route = ViewRouter.getPremiumSubscribeAnalyzeRoute({
                      subscribe_analyze_seq: payload?.subscribe_analyze_seq,
                    });
                    this.$mixpanel_track("View Premium-Report-Click Alert1");
                  } else {
                    route = ViewRouter.getSubscribeAnalyzeRoute({
                      subscribe_analyze_seq: payload?.subscribe_analyze_seq,
                    });
                    this.$mixpanel_track("View S/Report-analyzed-Click Alert1");
                  }
                  break;
                case PUSH_NOTI_ACTION.SUBSCRIBE.CHANGE_FINALIZED:
                  if ([true, "true"].includes(payload?.is_premium)) {
                    route = ViewRouter.getPremiumSubscribeAnalyzeRoute({
                      subscribe_analyze_seq: payload?.subscribe_analyze_seq,
                    });
                    this.$mixpanel_track("View Premium-Report-Click Alert2");
                  } else {
                    route = ViewRouter.getSubscribeAnalyzeRoute({
                      subscribe_analyze_seq: payload?.subscribe_analyze_seq,
                    });
                    this.$mixpanel_track("View S/Report-analyzed-Click Alert2");
                  }
                  break;
                case PUSH_NOTI_ACTION.DOBY.CONTRACT_AMEND_REQUEST:
                  route = ViewRouter.getDobyDashboardRoute({
                    encrypted_doby_seq: payload?.encrypted_doby_seq,
                  });
                  this.$mixpanel_track("View NzscPay-RequestFix-Click Alert1");
                  break;
                case PUSH_NOTI_ACTION.DOBY.TRANSFER_COMPLETED:
                  route = ViewRouter.getDobyDashboardRoute({
                    encrypted_doby_seq: payload?.encrypted_doby_seq,
                  });
                  this.$mixpanel_track("View NzscPay-Complete-Click Alert");
                  break;
                case PUSH_NOTI_ACTION.DOBY.CONTRACT_UPLOAD_REQUEST:
                  route = ViewRouter.getDobyDashboardRoute({
                    encrypted_doby_seq: payload?.encrypted_doby_seq,
                  });
                  this.$mixpanel_track("View NzscPay-Reload-Click Alert1");
                  break;
                case PUSH_NOTI_ACTION.DOBY.CONTRACT_NOT_UPLOADED:
                  route = ViewRouter.getDobyDashboardRoute({
                    encrypted_doby_seq: payload?.encrypted_doby_seq,
                  });
                  if (payload?.doby_status === DOBY_STATUS.REJECT) {
                    // 보완 요청.
                    this.$mixpanel_track("View NzscPay-RequestFix-Click Alert2");
                  } else if (payload?.doby_status === DOBY_STATUS.REVIEW) {
                    // 계약서 미등록.
                    this.$mixpanel_track("View NzscPay-Reload-Click Alert2");
                  }

                  break;
                case PUSH_NOTI_ACTION.DOBY.CARD_REGISTRATION_NUDGE:
                  route = ViewRouter.getDobyDashboardRoute({
                    encrypted_doby_seq: payload?.encrypted_doby_seq,
                  });
                  this.$mixpanel_track("View NzscPay-Card-Sub-Click Alert");
                  break;
              }

              if (route) {
                this.$router.push(route);
              }
            }
          };
        }
      }
    },
    fnSetupWebViewMessageHandler() {
      // 기존 리스너가 있으면 먼저 제거 (중복 방지)
      this.fnCleanupWebViewMessageHandler();
      window.addEventListener("message", this.fnWebViewMessageHandler);
    },
    fnCleanupWebViewMessageHandler() {
      // 도메인 변경 시에도 안전하게 제거
      try {
        window.removeEventListener("message", this.fnWebViewMessageHandler);
      } catch (e) {
        // 이미 제거되었거나 에러 발생 시 무시
      }
    },
    async fnFeatureFlag() {
      this.featureFlag_notificationPopup = await this.acEvalFeatureFlag({
        feature_key: "2025.09:notification_popup",
      });
    },
  },
};
</script>
<style>
.fade-enter {
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease-out;
}

.fade-leave-to {
  opacity: 0;
}

@media screen and (max-width: 1080px) {
  .slide-fade-enter {
    transform: translateX(10px);
    opacity: 0;
  }

  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.1s ease;
  }

  .slide-fade-leave-to {
    transform: translateX(-10px);
    opacity: 0;
  }
}
</style>
